import { DateTime, Settings } from 'luxon';
import { HOME_TIME_ZONE } from './constants';

// NOTE: We need to set the system timezone to avoid incorrect date calculations when using methods like `DateTime.now()`.
Settings.defaultZone = HOME_TIME_ZONE;

export interface WeekYear {
  week: number;
  year: number;
}

export abstract class WeekYear {
  static subscriptionFrequencySkipMap = {
    ONE_WEEK: 1,
    TWO_WEEKS: 2,
    THREE_WEEKS: 3,
    FOUR_WEEKS: 4
  };

  static forNow(): WeekYear {
    return WeekYear.forDateTime(DateTime.now());
  }

  static forDateTime(date: DateTime): WeekYear {
    const d = date.setZone(HOME_TIME_ZONE);
    if (d.weekday < 6) {
      return { year: d.weekYear, week: d.weekNumber };
    }
    const nextWeek = d.plus({ weeks: 1 });
    return { week: nextWeek.weekNumber, year: nextWeek.weekYear };
  }

  static completeOrderDate({ week, year }: WeekYear): DateTime {
    return DateTime.fromObject(
      { weekNumber: week, weekYear: year, weekday: 5, hour: 23, minute: 59, second: 59 }, // 11:59:59 PM New York time on Friday
      { zone: HOME_TIME_ZONE }
    );
  }

  // Saturday
  static createMiseOrderDate({ week, year }: WeekYear): DateTime {
    return DateTime.fromObject(
      { weekNumber: week, weekYear: year, weekday: 6, hour: 0, minute: 0, second: 0 },
      { zone: HOME_TIME_ZONE }
    );
  }

  // Friday of the previous week.
  static generateCartDate({ week, year }: WeekYear): DateTime {
    return DateTime.fromObject(
      { weekNumber: week, weekYear: year, weekday: 5, hour: 0, minute: 0, second: 0 },
      { zone: HOME_TIME_ZONE }
    ).minus({ week: 1 });
  }

  static shippingDate({ week, year }: WeekYear): DateTime {
    return DateTime.fromObject(
      { weekNumber: week, weekYear: year, weekday: 1, hour: 16, minute: 0, second: 0 }, // 4 PM New York time on Monday
      { zone: HOME_TIME_ZONE }
    ).plus({ week: 1 });
  }

  static cancelPendingOrdersDate({ week, year }: WeekYear): DateTime {
    return DateTime.fromObject(
      { weekNumber: week, weekYear: year, weekday: 1, hour: 3, minute: 1, second: 0 }, // 3:01 AM New York time on Monday
      { zone: HOME_TIME_ZONE }
    ).plus({ week: 1 });
  }

  static toString({ week, year }: WeekYear): string {
    return `${year.toLocaleString('en-US', {
      maximumSignificantDigits: 4,
      minimumIntegerDigits: 4,
      useGrouping: false
    })}-${week.toLocaleString('en-US', {
      minimumIntegerDigits: 2,
      maximumSignificantDigits: 2,
      useGrouping: false
    })}`;
  }
}
